import React, { useState, useEffect } from 'react';
import { Filter, ChevronDown, ChevronUp } from 'lucide-react';
import apiService from '../services/api';

export default function SegmentFilters({ filters, onChange }) {
  const [expanded, setExpanded] = useState(false);
  const [locations, setLocations] = useState([]);

  useEffect(() => {
    apiService.get('/amn/locations/').then(data => {
      const list = Array.isArray(data) ? data : data.results || [];
      setLocations(list);
    }).catch(() => {});
  }, []);

  const f = filters || {};

  const updateFilter = (key, value) => {
    const next = { ...f, [key]: value };
    // Clean up empty values
    if (value === '' || value === null || value === undefined || (Array.isArray(value) && value.length === 0)) {
      delete next[key];
    }
    onChange(Object.keys(next).length > 0 ? next : null);
  };

  const toggleClientType = (type) => {
    const current = f.client_type || [];
    const next = current.includes(type) ? current.filter(t => t !== type) : [...current, type];
    updateFilter('client_type', next);
  };

  const activeCount = Object.keys(f).filter(k => {
    const v = f[k];
    return v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0);
  }).length;

  return (
    <div className="border-t pt-3 mt-1">
      <button type="button" onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-xs font-medium text-gray-700 hover:text-red-800 transition-colors">
        <Filter className="w-3.5 h-3.5" />
        Segment Filters
        {activeCount > 0 && (
          <span className="px-1.5 py-0.5 bg-red-800 text-white rounded-full text-xs leading-none">{activeCount}</span>
        )}
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {expanded && (
        <div className="mt-2 space-y-2.5 pl-1">
          {/* Client Type */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Client Type</label>
            <div className="flex gap-1.5">
              {[{ value: 'home', label: 'Home' }, { value: 'alf', label: 'ALF' }, { value: 'event', label: 'Event' }].map(ct => (
                <button key={ct.value} type="button" onClick={() => toggleClientType(ct.value)}
                  className={`px-2 py-0.5 rounded text-xs border transition-colors ${
                    (f.client_type || []).includes(ct.value)
                      ? 'bg-red-800 text-white border-red-800'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-red-800'
                  }`}>
                  {ct.label}
                </button>
              ))}
            </div>
          </div>

          {/* Location */}
          {locations.length > 0 && (
            <div>
              <label className="block text-xs text-gray-500 mb-1">Location</label>
              <select
                value={f.location_ids && f.location_ids.length === 1 ? f.location_ids[0] : ''}
                onChange={e => updateFilter('location_ids', e.target.value ? [parseInt(e.target.value)] : [])}
                className="w-full px-2 py-1 text-xs border rounded outline-none focus:ring-1 focus:ring-red-800">
                <option value="">All locations</option>
                {locations.map(loc => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Last Appointment */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Last Appointment</label>
            <div className="grid grid-cols-2 gap-1.5">
              <div>
                <label className="text-xs text-gray-400">After</label>
                <input type="date" value={f.last_appointment_after || ''}
                  onChange={e => updateFilter('last_appointment_after', e.target.value)}
                  className="w-full px-2 py-1 text-xs border rounded outline-none focus:ring-1 focus:ring-red-800" />
              </div>
              <div>
                <label className="text-xs text-gray-400">Before</label>
                <input type="date" value={f.last_appointment_before || ''}
                  onChange={e => updateFilter('last_appointment_before', e.target.value)}
                  className="w-full px-2 py-1 text-xs border rounded outline-none focus:ring-1 focus:ring-red-800" />
              </div>
            </div>
          </div>

          {/* Total Spent */}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Total Spent ($)</label>
            <div className="grid grid-cols-2 gap-1.5">
              <div>
                <label className="text-xs text-gray-400">Min</label>
                <input type="number" value={f.total_spent_min || ''} min="0" step="50"
                  onChange={e => updateFilter('total_spent_min', e.target.value ? parseFloat(e.target.value) : '')}
                  placeholder="0"
                  className="w-full px-2 py-1 text-xs border rounded outline-none focus:ring-1 focus:ring-red-800" />
              </div>
              <div>
                <label className="text-xs text-gray-400">Max</label>
                <input type="number" value={f.total_spent_max || ''} min="0" step="50"
                  onChange={e => updateFilter('total_spent_max', e.target.value ? parseFloat(e.target.value) : '')}
                  placeholder="No limit"
                  className="w-full px-2 py-1 text-xs border rounded outline-none focus:ring-1 focus:ring-red-800" />
              </div>
            </div>
          </div>

          {activeCount > 0 && (
            <button type="button" onClick={() => onChange(null)}
              className="text-xs text-red-600 hover:underline">
              Clear all filters
            </button>
          )}
        </div>
      )}
    </div>
  );
}
